import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'

import { CircleFlag } from 'react-circle-flags'

import countries from 'i18n-iso-countries'
import ar from 'i18n-iso-countries/langs/ar.json'
import en from 'i18n-iso-countries/langs/en.json'

import { z } from 'zod'

import parsePhoneNumber, { getCountryCallingCode, isValidPhoneNumber } from 'libphonenumber-js'

import { Check, ChevronsUpDown, GlobeIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

import { SmartButton } from '@/components/ui-custom/SmartButton'

countries.registerLocale(en)
countries.registerLocale(ar)

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [breakpoint])

  return isMobile
}

export const phoneSchema = z.string().refine((value) => {
  try {
    return isValidPhoneNumber(value)
  } catch {
    return false
  }
}, 'Invalid phone number')

export type CountryData = {
  alpha2: string
  name: string
  callingCode: string
}

interface PhoneChangePayload {
  fullNumber: string
  callingCode: string
  numberWithoutCode: string
}

interface PhoneInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  onCountryChange?: (data: CountryData | undefined) => void
  value?: string
  onChange?: (payload: PhoneChangePayload) => void
  placeholder?: string
  defaultCountry?: string
  className?: string
  locale?: string
  excludeCountries?: string[]
  onlyCountries?: string[]
  preferredCountries?: string[]
  isRTL: boolean
}

// Flag
const CountryFlag = ({ code }: { code: string }) => {
  if (!code) return null
  return <CircleFlag countryCode={code.toLowerCase()} height='20' />
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      className,
      onCountryChange,
      onChange,
      value,
      placeholder,
      locale = 'en',
      defaultCountry = 'US',
      excludeCountries = [],
      onlyCountries,
      preferredCountries = [],
      isRTL = false,
      ...props
    },
    ref
  ) => {
    // Generate country list from i18n-iso-countries
    const allCountries: CountryData[] = useMemo(() => {
      const names = countries.getNames(locale, { select: 'official' })
      return Object.entries(names)
        .map(([alpha2, name]) => {
          try {
            return {
              alpha2,
              name,
              callingCode: '+' + getCountryCallingCode(alpha2 as any),
            }
          } catch {
            return null // skip countries without calling codes
          }
        })
        .filter(Boolean) as CountryData[]
    }, [locale])
    // Filter countries according to props
    const filteredCountries = useMemo(() => {
      let list = allCountries
      if (excludeCountries?.length) {
        const excludeSet = new Set(excludeCountries.map((c) => c.toUpperCase()))
        list = list.filter((c) => !excludeSet.has(c.alpha2))
      }
      if (onlyCountries?.length) {
        const onlySet = new Set(onlyCountries.map((c) => c.toUpperCase()))
        list = list.filter((c) => onlySet.has(c.alpha2))
      }
      return list
    }, [allCountries, onlyCountries, excludeCountries])

    const preferredCountryList = useMemo(() => {
      if (!preferredCountries?.length) return []
      const prefSet = new Set(preferredCountries.map((c) => c.toUpperCase()))
      return filteredCountries.filter((c) => prefSet.has(c.alpha2))
    }, [preferredCountries, filteredCountries])

    const otherCountries = useMemo(() => {
      const prefSet = new Set(preferredCountries.map((c) => c.toUpperCase()))
      return filteredCountries.filter((c) => !prefSet.has(c.alpha2))
    }, [preferredCountries, filteredCountries])

    // Selected country state
    const [countryData, setCountryData] = useState<CountryData>(
      filteredCountries.find((c) => c.alpha2 === defaultCountry.toUpperCase()) ?? filteredCountries[0]
    )
    const [displayFlag, setDisplayFlag] = useState<string>(countryData?.alpha2.toLowerCase() || '')
    const [open, setOpen] = useState(false)
    const [internalValue, setInternalValue] = useState(value || '')

    // Initialize input value with default country calling code
    useEffect(() => {
      if (!value) return

      try {
        const parsed = parsePhoneNumber(value)
        if (parsed && parsed.country) {
          const detected = filteredCountries.find((c) => c.alpha2 === parsed.country)
          if (detected) {
            setCountryData(detected)
            setDisplayFlag(detected.alpha2.toLowerCase())
            onCountryChange?.(detected)
          }
          setInternalValue(parsed.formatInternational())
        } else {
          // If cannot parse, fall back to default with raw value
          setInternalValue(value)
        }
      } catch {
        setInternalValue(value)
      }
    }, [value, filteredCountries, onCountryChange])

    const handleCountrySelect = useCallback(
      (alpha2: string) => {
        const newCountry = filteredCountries.find((c) => c.alpha2 === alpha2)
        if (!newCountry || newCountry.alpha2 === countryData.alpha2) return

        setCountryData(newCountry)
        setDisplayFlag(newCountry.alpha2.toLowerCase())
        onCountryChange?.(newCountry)

        let newNumber = internalValue
        let formattedNumber = ''

        try {
          const parsed = parsePhoneNumber(internalValue)
          if (parsed) {
            // Replace existing country code with the new one
            const nationalNumber = parsed.nationalNumber
            const replacedNumber = '+' + newCountry.callingCode.replace(/^\+/, '') + nationalNumber
            const parsedNew = parsePhoneNumber(replacedNumber)
            if (parsedNew) formattedNumber = parsedNew.formatInternational()
            newNumber = parsedNew?.number || replacedNumber
          } else {
            // If parsing fails, prepend new code and format
            const replacedNumber =
              '+' + newCountry.callingCode.replace(/^\+/, '') + internalValue.replace(/^\+?[0-9]*/, '')
            const parsedNew = parsePhoneNumber(replacedNumber)
            formattedNumber = parsedNew?.formatInternational() || replacedNumber
            newNumber = parsedNew?.number || replacedNumber
          }
        } catch {
          // fallback
          const replacedNumber =
            '+' + newCountry.callingCode.replace(/^\+/, '') + internalValue.replace(/^\+?[0-9]*/, '')
          formattedNumber = replacedNumber
          newNumber = replacedNumber
        }

        onChange?.({
          fullNumber: newNumber,
          callingCode: newCountry.callingCode,
          numberWithoutCode: newNumber.replace(newCountry.callingCode, ''),
        })

        setInternalValue(formattedNumber)
        setOpen(false)
      },
      [filteredCountries, countryData, internalValue, onChange, onCountryChange]
    )

    const callingCode = useMemo(() => countryData.callingCode || '', [countryData])

    const handlePhoneChangeWithDisplay = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        let raw = e.target.value
        // Allow only phone-related characters
        raw = raw.replace(/[^\d+()\s-]/g, '')

        let formattedNumber = raw
        let outputNumber = ''
        let detectedCountry = countryData
        try {
          const parsed = parsePhoneNumber(raw)
          if (parsed) {
            // Auto-detect country
            if (parsed.country && parsed.country !== countryData.alpha2) {
              const newCountry = filteredCountries.find((c) => c.alpha2 === parsed.country)
              if (newCountry) {
                detectedCountry = newCountry
                setCountryData(newCountry)
                setDisplayFlag(newCountry.alpha2.toLowerCase())
                onCountryChange?.(newCountry)
              }
            }
            formattedNumber = parsed.formatInternational()
            outputNumber = parsed.number // raw international number
          }
        } catch {
          // ignore parse errors, fallback to raw
        }
        // Prevent deleting the calling code
        const callingCode = detectedCountry.callingCode
        if (!formattedNumber.startsWith(callingCode)) {
          formattedNumber = callingCode + raw.replace(/[^\d+()\s-]/g, '')
          const parsed = parsePhoneNumber(formattedNumber)
          if (parsed) {
            formattedNumber = parsed.formatInternational()
            outputNumber = parsed.number
          }
        }

        const numberWithoutCode = outputNumber.replace(detectedCountry.callingCode, '')

        onChange?.({
          fullNumber: outputNumber,
          callingCode,
          numberWithoutCode,
        })

        setInternalValue(formattedNumber)
      },
      [callingCode, onChange]
    )

    const countryItems = useMemo(() => {
      const renderItems = (list: CountryData[]) =>
        list.map((c) => (
          <CommandItem
            key={c.alpha2}
            onSelect={() => handleCountrySelect(c.alpha2)}
            className='flex w-full cursor-pointer items-center justify-between gap-2'
          >
            <div className='flex min-w-0 flex-1 items-center gap-2 overflow-hidden'>
              <div className='h-5 w-5 shrink-0 overflow-hidden rounded-full'>
                <CountryFlag code={c.alpha2} />
              </div>
              <span className='truncate'>{c.name}</span>
            </div>
            <span className='text-muted-foreground ml-auto'>{c.callingCode}</span>
            <Check
              className={cn('ml-2 h-4 w-4 shrink-0', countryData.alpha2 === c.alpha2 ? 'opacity-100' : 'opacity-0')}
            />
          </CommandItem>
        ))

      return (
        <>
          {preferredCountryList.length > 0 && (
            <>
              <CommandGroup heading='Preferred countries'>{renderItems(preferredCountryList)}</CommandGroup>
              <CommandSeparator />
            </>
          )}
          <CommandGroup heading='All countries'>{renderItems(otherCountries)}</CommandGroup>
        </>
      )
    }, [preferredCountryList, otherCountries, countryData.alpha2, handleCountrySelect])

    return (
      <div
        className={cn(
          'border-input bg-background ring-offset-background focus-within:ring-ring flex h-10 w-full items-center rounded-md border text-sm focus-within:ring-1 focus-within:outline-none',
          '[&:has(input[aria-invalid=true])]:border-destructive',
          className
        )}
      >
        <div className='flex h-full items-center border-r px-2'>
          {useIsMobile() ? (
            <>
              <button
                type='button'
                className='flex h-full min-w-[3rem] cursor-pointer items-center gap-1'
                onClick={() => setOpen(true)}
              >
                <div className='h-5 w-5 overflow-hidden rounded-full'>
                  {displayFlag ? <CountryFlag code={countryData.alpha2} /> : <GlobeIcon size={16} />}
                </div>
                <ChevronsUpDown className='text-muted-foreground h-4 w-4' />
              </button>

              {open && (
                <div className='bg-background/95 fixed inset-0 z-[999] flex flex-col backdrop-blur-sm'>
                  <div className='flex items-center justify-between border-b p-4'>
                    <h2 className='text-lg font-medium'>Select Country</h2>
                    <SmartButton variant={'ghost'} onClick={() => setOpen(false)}>
                      Close
                    </SmartButton>
                  </div>
                  <div className='flex-1 overflow-auto'>
                    <Command className='w-full'>
                      <CommandInput placeholder='Search country...' />
                      <CommandList>{countryItems}</CommandList>
                    </Command>
                  </div>
                </div>
              )}
            </>
          ) : (
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild className='h-full min-w-[3rem] cursor-pointer'>
                <div className='flex items-center gap-1'>
                  <div className='h-5 w-5 overflow-hidden rounded-full'>
                    {displayFlag ? <CountryFlag code={countryData.alpha2} /> : <GlobeIcon size={16} />}
                  </div>
                  <ChevronsUpDown className='text-muted-foreground h-4 w-4' />
                </div>
              </PopoverTrigger>
              <PopoverContent
                collisionPadding={10}
                side='bottom'
                className='-ml-2 w-auto min-w-[var(--radix-popper-anchor-width)] p-0'
                align='start'
              >
                {open && (
                  <Command className='max-h-[200px] w-full max-w-[380px] min-w-[300px] sm:max-h-[270px]'>
                    <CommandList className='scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-200'>
                      <div className='bg-popover sticky top-0 z-10'>
                        <CommandInput placeholder='Search country...' />
                      </div>
                      <CommandEmpty>No country found.</CommandEmpty>
                      {countryItems}
                    </CommandList>
                  </Command>
                )}
              </PopoverContent>
            </Popover>
          )}
        </div>
        <input
          ref={ref}
          value={internalValue.startsWith(callingCode) ? internalValue : callingCode}
          onChange={handlePhoneChangeWithDisplay}
          placeholder={placeholder || 'Enter number'}
          type='tel'
          autoComplete='tel'
          name='phone'
          dir={isRTL ? 'rtl' : 'ltr'}
          maxLength={25}
          className={cn(
            'placeholder:text-muted-foreground flex h-9 w-full border-none bg-transparent px-1 py-1 text-start text-base leading-none transition-colors outline-none md:text-sm',
            className
          )}
          {...props}
        />
      </div>
    )
  }
)

PhoneInput.displayName = 'PhoneInput'
